<template>
  <EXTooltip
    v-if="tooltipText"
    :title="tooltipText"
    placement="top"
    :delay="100"
  >
    <div class="as-employee-inspection-status-badge" :class="rootClass">
      <div v-if="showAvatar" class="as-employee-inspection-status-badge__leading">
        <img
          v-if="avatarUrl"
          :src="avatarUrl"
          alt=""
          class="as-employee-inspection-status-badge__avatar"
        />
      </div>
      <div
        v-else
        class="as-employee-inspection-status-badge__leading as-employee-inspection-status-badge__leading--icon"
      >
        <img
          :src="searchIconSrc"
          alt=""
          class="as-employee-inspection-status-badge__search-icon"
        />
      </div>
      <span class="as-employee-inspection-status-badge__label">{{ displayLabel }}</span>
    </div>
  </EXTooltip>
  <div v-else class="as-employee-inspection-status-badge" :class="rootClass">
    <div v-if="showAvatar" class="as-employee-inspection-status-badge__leading">
      <img
        v-if="avatarUrl"
        :src="avatarUrl"
        alt=""
        class="as-employee-inspection-status-badge__avatar"
      />
    </div>
    <div
      v-else
      class="as-employee-inspection-status-badge__leading as-employee-inspection-status-badge__leading--icon"
    >
      <img
        :src="searchIconSrc"
        alt=""
        class="as-employee-inspection-status-badge__search-icon"
      />
    </div>
    <span class="as-employee-inspection-status-badge__label">{{ displayLabel }}</span>
  </div>
</template>

<script setup>
import { computed } from "vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";

const searchIconSrc = require("@components/assets/search-refraction.svg");

const props = defineProps({
  status: {
    type: String,
    required: true,
    validator(value) {
      return ["passed", "failed", "na", "waiting_for_coach"].includes(value);
    },
  },
  /** Shown for passed, failed, and na. */
  avatarUrl: {
    type: String,
    default: "",
  },
  /** Overrides the default label for the status. */
  label: {
    type: String,
    default: "",
  },
  /** Coach reason for passed, failed, or N/A — shown on hover when set. */
  reason: {
    type: String,
    default: "",
  },
});

const normalizedStatus = computed(() => props.status);

const defaultLabels = {
  passed: "Inspection Passed",
  failed: "Inspection Failed",
  na: "Inspection N/A",
  waiting_for_coach: "Waiting for Coach",
};

const displayLabel = computed(() => {
  if (props.label) return props.label;
  return defaultLabels[normalizedStatus.value] || "";
});

const showAvatar = computed(() => normalizedStatus.value !== "waiting_for_coach");

const tooltipText = computed(() => {
  const reason = String(props.reason ?? "").trim();
  if (!reason) return "";
  if (["passed", "failed", "na"].includes(normalizedStatus.value)) return reason;
  return "";
});

const rootClass = computed(() => ({
  "as-employee-inspection-status-badge--passed": normalizedStatus.value === "passed",
  "as-employee-inspection-status-badge--failed": normalizedStatus.value === "failed",
  "as-employee-inspection-status-badge--na": normalizedStatus.value === "na",
  "as-employee-inspection-status-badge--waiting": normalizedStatus.value === "waiting_for_coach",
}));
</script>

<style scoped>
.as-employee-inspection-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 6px;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  border: 1px solid transparent;
  box-sizing: border-box;
}

.as-employee-inspection-status-badge__leading {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.as-employee-inspection-status-badge__leading--icon {
  overflow: visible;
}

.as-employee-inspection-status-badge__avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.as-employee-inspection-status-badge__search-icon {
  width: 16px;
  height: 16px;
  display: block;
}

.as-employee-inspection-status-badge__label {
  padding-right: 2px;
}

/* Passed */
.as-employee-inspection-status-badge--passed {
  background-color: #ecfdf3;
  border-color: #aaefc6;
  color: #067647;
}

/* Failed */
.as-employee-inspection-status-badge--failed {
  background-color: #fef3f2;
  border-color: #FECDCA;
  color: #b42318;
}

/* N/A */
.as-employee-inspection-status-badge--na {
  background-color: #eaecf0;
  border-color: #666666;
  color: #000000;
}

/* Waiting for coach */
.as-employee-inspection-status-badge--waiting {
  background-color: #F0F9FF;
  border-color: #B9E6FE;
  color: #026AA2;
}
</style>
